import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  SimpleChanges,
} from '@angular/core';

import {
  SearchOrigin,
  SearchSubmitMethod,
  SearchTrackerService,
} from '@app/search/services/search-tracker.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { AuthService } from '@app/shared/services/auth.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { ProviderInfo } from '@app/manage-providers/provider-api.model';
import { SearchUrlService } from '@dg/shared-services';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';

export type AddMenuFnType = (provider: any) => MenuViewModel[];

@Component({
  selector: 'dgx-flex-provider-card',
  templateUrl: './flex-provider-card.component.html',
  styleUrls: ['./flex-provider-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlexProviderCardComponent implements OnInit {
  @Input() public provider: ProviderInfo;
  @Input() public isEditing: boolean;
  @Input() public addMenuFn?: AddMenuFnType;
  /** Used to display a preview of the provider card with the brand color on the settings page. */
  @Input() public previewBrandColor?: string;
  /** Used to display a preview of the provider card with the brand image on the settings page. */
  @Input() public previewBrandImage?: string;

  public providerUrl: string;
  public providerImageUrl: string;
  public brandColor: string;
  public menuConfig: MenuViewModel[];

  public showPlanSimplification = this.ldFlagsService.showPlanSimplification;

  constructor(
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private ldFlagsService: LDFlagsService,
    private searchTrackerService: SearchTrackerService,
    private tracker: TrackerService,
    private webEnvironmentService: WebEnvironmentService,
    private searchUrlService: SearchUrlService
  ) {}

  public ngOnInit(): void {
    if (this.showPlanSimplification) {
      if (this.previewBrandColor) {
        this.brandColor = this.previewBrandColor;
      } else {
        const authUser = this.authService.authUser;
        const orgBranding = authUser?.defaultOrgInfo?.organizationBranding;
        if (this.provider.providerId === -1 && orgBranding?.brandColor) {
          this.brandColor = orgBranding?.brandColor;
        }
      }
    }

    this.setProviderUrl();
    this.setProviderImageUrl();

    if (this.addMenuFn) {
      this.menuConfig = this.addMenuFn(this.provider);
    }

    this.cdr.markForCheck();
  }

  public ngOnChanges({
    previewBrandColor,
    previewBrandImage,
  }: SimpleChanges): void {
    if (this.showPlanSimplification) {
      if (previewBrandColor) {
        this.brandColor = previewBrandColor?.currentValue;
      }
      if (previewBrandImage) {
        this.providerImageUrl = previewBrandImage?.currentValue;
      }

      this.cdr.markForCheck();
    }
  }

  public trackProvider() {
    this.tracker.trackEventData({
      action: 'Featured Provider Clicked',
      category: 'Browse',
      properties: {
        providerName: this.provider.providerName,
      },
    });
  }

  public trackLinkClicked() {
    this.searchTrackerService.setSearchData({
      submitMethod: SearchSubmitMethod.browseProvider,
    });

    this.searchTrackerService.searchInitiated({
      searchTerm: this.provider.providerName,
      origin: SearchOrigin.providerBubble,
    });
  }

  private setProviderUrl() {
    this.providerUrl = this.searchUrlService.getProviderSearchURL(
      this.provider.providerId
    );
  }

  private setProviderImageUrl() {
    const providerImg = this.provider.imageInfo;
    const imageInfoPath =
      providerImg?.png ?? providerImg?.svg ?? providerImg?.favicon;

    this.providerImageUrl = imageInfoPath
      ? this.webEnvironmentService.getBlobUrl(imageInfoPath)
      : this.webEnvironmentService.getBlobUrl(
          '/content/img/app/no-logo-seal.png'
        );
  }
}
